import { gql } from "@apollo/client";

// ============================================================
// SEO METADATA QUERIES
// ============================================================

/**
 * Get SEO metadata for a specific page (PUBLIC - no auth required)
 * Used by SEOHead component and SEOPreviewTool
 * Only returns published metadata
 */
export const GET_SEO_METADATA = gql`
  query GetSEOMetadata($pagePath: String!) {
    seoMetadata(pagePath: $pagePath) {
      id
      pagePath
      pageTitle
      metaTitle
      metaDescription
      metaKeywords
      canonicalUrl
      ogTitle
      ogDescription
      ogImage
      ogType
      twitterCard
      twitterTitle
      twitterDescription
      twitterImage
      linkedInImage
      linkedInTitle
      linkedInDescription
      pinterestImage
      pinterestTitle
      pinterestDescription
      trackPageViews
      conversionGoal
      robots
      structuredData
      published
      lastEditedBy
      updatedAt
    }
  }
`;

/**
 * Get SEO metadata for the preview tool (lighter query, no advanced fields)
 * Used by SEOPreviewTool for social media preview cards
 */
export const GET_SEO_METADATA_PREVIEW = gql`
  query GetSEOMetadataPreview($pagePath: String!) {
    seoMetadata(pagePath: $pagePath) {
      id
      pagePath
      pageTitle
      metaTitle
      metaDescription
      ogTitle
      ogDescription
      ogImage
      twitterTitle
      twitterDescription
      twitterImage
      linkedInTitle
      linkedInDescription
      linkedInImage
      pinterestTitle
      pinterestDescription
      pinterestImage
      published
    }
  }
`;

/**
 * Get SEO metadata for the SEOHead component (meta tags injection)
 * Fetches all fields needed for <head> tag rendering
 */
export const GET_SEO_METADATA_HEAD = gql`
  query GetSEOMetadataHead($pagePath: String!) {
    seoMetadata(pagePath: $pagePath) {
      id
      pagePath
      pageTitle
      metaTitle
      metaDescription
      metaKeywords
      canonicalUrl
      ogTitle
      ogDescription
      ogImage
      ogType
      twitterCard
      twitterTitle
      twitterDescription
      twitterImage
      linkedInImage
      linkedInTitle
      linkedInDescription
      pinterestImage
      pinterestTitle
      pinterestDescription
      robots
      structuredData
    }
  }
`;

/**
 * Get all SEO metadata (ADMIN - requires auth)
 * Used by SEOMetadataList for the admin list view
 */
export const GET_ALL_SEO_METADATA = gql`
  query GetAllSEOMetadata($filters: SEOMetadataFilters) {
    allSEOMetadata(filters: $filters) {
      id
      pagePath
      pageTitle
      metaTitle
      metaDescription
      published
      lastEditedBy
      updatedAt
      createdAt
    }
  }
`;

// ============================================================
// SEO METADATA MUTATIONS
// ============================================================

export const CREATE_SEO_METADATA = gql`
  mutation CreateSEOMetadata($input: CreateSEOMetadataInput!) {
    createSEOMetadata(input: $input) {
      id
      pagePath
    }
  }
`;

export const UPDATE_SEO_METADATA = gql`
  mutation UpdateSEOMetadata($pagePath: String!, $input: UpdateSEOMetadataInput!) {
    updateSEOMetadata(pagePath: $pagePath, input: $input) {
      id
      pagePath
    }
  }
`;

export const DELETE_SEO_METADATA = gql`
  mutation DeleteSEOMetadata($pagePath: String!) {
    deleteSEOMetadata(pagePath: $pagePath)
  }
`;

export const PUBLISH_SEO_METADATA = gql`
  mutation PublishSEOMetadata($pagePath: String!) {
    publishSEOMetadata(pagePath: $pagePath) {
      id
      published
    }
  }
`;

export const UNPUBLISH_SEO_METADATA = gql`
  mutation UnpublishSEOMetadata($pagePath: String!) {
    unpublishSEOMetadata(pagePath: $pagePath) {
      id
      published
    }
  }
`;

export const UPLOAD_SEO_IMAGE = gql`
  mutation UploadSEOImage($file: Upload!) {
    uploadSEOImage(file: $file) {
      url
      hash
      size
      filename
    }
  }
`;
